import React from 'react';
import { createPortal } from 'react-dom';
import './V2Select.css';

type SelectOption<T> = { value: T; label: string; disabled?: boolean };

export default function V2Select<T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
  disabled = false,
  className = '',
}: {
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
}) {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const listId = React.useId();
  const searchRef = React.useRef({ text: '', time: 0 });
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const [position, setPosition] = React.useState({ left: 0, top: 0, width: 168, maxHeight: 320 });
  const selected = options.findIndex((option) => option.value === value);
  const expanded = open && !disabled;

  const showMenu = (fallback: 'first' | 'last' = 'first') => {
    const enabled = options
      .map((option, index) => (option.disabled ? -1 : index))
      .filter((index) => index >= 0);
    setActive(
      selected >= 0 && !options[selected].disabled
        ? selected
        : ((fallback === 'last' ? enabled[enabled.length - 1] : enabled[0]) ?? -1),
    );
    searchRef.current = { text: '', time: 0 };
    setOpen(true);
  };

  const choose = (index: number) => {
    const option = options[index];
    if (!option || option.disabled || disabled) return;
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
    if (option.value !== value) onChange(option.value);
  };

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportLeft = viewport?.offsetLeft ?? 0;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportWidth = viewport?.width ?? document.documentElement.clientWidth;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const width = Math.min(Math.max(168, rect.width), viewportWidth - 16);
    const below = viewportTop + viewportHeight - rect.bottom - 16;
    const above = rect.top - viewportTop - 16;
    const height = Math.min(menu.scrollHeight, 320, viewportHeight * 0.6);
    const upwards = below < height && above > below;
    const maxHeight = Math.max(0, Math.min(320, viewportHeight * 0.6, upwards ? above : below));
    const next = {
      width,
      maxHeight,
      left: Math.max(
        viewportLeft + 8,
        Math.min(rect.left, viewportLeft + viewportWidth - width - 8),
      ),
      top: upwards ? rect.top - 8 - Math.min(height, maxHeight) : rect.bottom + 8,
    };
    setPosition((current) =>
      Object.keys(next).every(
        (key) => current[key as keyof typeof next] === next[key as keyof typeof next],
      )
        ? current
        : next,
    );
  }, []);

  React.useLayoutEffect(() => {
    if (expanded) updatePosition();
  }, [expanded, options, updatePosition]);

  React.useLayoutEffect(() => {
    if (!expanded || active < 0) return;
    const menu = menuRef.current;
    const option = menu?.children[active] as HTMLElement | undefined;
    if (!menu || !option) return;
    if (option.offsetTop < menu.scrollTop) menu.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > menu.scrollTop + menu.clientHeight)
      menu.scrollTop = option.offsetTop + option.offsetHeight - menu.clientHeight;
  }, [active, expanded, position.maxHeight, options]);

  React.useEffect(() => {
    if (!expanded) return;
    const outside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !triggerRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      )
        setOpen(false);
    };
    const scroll = (event: Event) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('resize', updatePosition);
    };
  }, [expanded, updatePosition]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const enabled = options
      .map((option, index) => (option.disabled ? -1 : index))
      .filter((index) => index >= 0);
    if (event.key === 'Escape' && expanded) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    } else if (event.key === 'Tab') setOpen(false);
    else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (expanded) choose(active);
      else showMenu();
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      if (!expanded) showMenu(event.key === 'ArrowUp' || event.key === 'End' ? 'last' : 'first');
      if (event.key === 'Home') setActive(enabled[0] ?? -1);
      else if (event.key === 'End') setActive(enabled[enabled.length - 1] ?? -1);
      else if (expanded && enabled.length) {
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        setActive(enabled[(enabled.indexOf(active) + direction + enabled.length) % enabled.length]);
      }
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const now = Date.now();
      const text =
        (now - searchRef.current.time < 700 ? searchRef.current.text : '') +
        event.key.toLocaleLowerCase();
      searchRef.current = { text, time: now };
      const query = [...text].every((letter) => letter === text[0]) ? text[0] : text;
      const ordered = [
        ...enabled.filter((index) => index > active),
        ...enabled.filter((index) => index <= active),
      ];
      const match = ordered.find((index) =>
        options[index].label.trim().toLocaleLowerCase().startsWith(query),
      );
      if (match !== undefined) {
        setOpen(true);
        setActive(match);
      }
    }
  };

  const portalTarget = triggerRef.current?.closest('dialog') ?? document.body;
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        className={`v2-select ${className}`}
        aria-label={ariaLabel}
        aria-expanded={expanded}
        aria-haspopup="listbox"
        aria-controls={expanded ? listId : undefined}
        aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
        disabled={disabled}
        onClick={() => (expanded ? setOpen(false) : showMenu())}
        onKeyDown={onKeyDown}
        onBlur={() => setOpen(false)}
      >
        <span className="v2-select__value">{options[selected]?.label ?? '—'}</span>
        <span className="v2-select__chevron" aria-hidden="true">
          ↓
        </span>
      </button>
      {expanded
        ? createPortal(
            <div
              ref={menuRef}
              id={listId}
              className="v2-select-menu"
              role="listbox"
              aria-label={ariaLabel}
              style={position}
            >
              {options.map((option, index) => (
                <div
                  key={`${typeof option.value}:${option.value}`}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={option.value === value}
                  aria-disabled={option.disabled || undefined}
                  data-active={active === index || undefined}
                  className="v2-select-menu__option"
                  onPointerMove={() => {
                    if (!option.disabled) setActive(index);
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(index)}
                >
                  {option.label}
                </div>
              ))}
            </div>,
            portalTarget,
          )
        : null}
    </>
  );
}
